/**
 * recuperacion.ts — leer con qué llega alguien que hizo clic en un correo.
 *
 * Books manda dos correos con enlace: la **invitación** (E8.1, cuando el
 * operador da de alta a una persona) y la **recuperación de contraseña**. Los
 * dos terminan en la misma pantalla y con el mismo problema: hay que averiguar
 * qué trae la URL antes de poder hacer nada.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ ESTO ES UNA FUNCIÓN PURA, Y NO POR GUSTO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Recibe una URL como texto y devuelve qué trae. Ni toca `window`, ni llama a
 * Supabase, ni sabe qué es React. Igual que `flujo.ts`: lo que decide algo se
 * prueba sin navegador, porque un enlace de recuperación mal leído solo se
 * descubre con un correo real en la mano, y eso no se hace veinte veces.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ POR QUÉ HAY QUE MIRAR EN DOS SITIOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Supabase manda el enlace de dos formas según cómo esté escrita la plantilla
 * del correo, y las dos son legítimas:
 *
 *   · `?token_hash=…&type=recovery`  ← plantilla con `{{ .TokenHash }}`
 *   · `#access_token=…&type=recovery` ← plantilla por omisión
 *
 * Se aceptan las dos y **se prefiere la primera**, porque un `token_hash` es
 * un vale de un solo uso que hay que canjear contra Supabase, mientras que el
 * fragmento trae la credencial ya hecha.
 *
 * Que el segundo llegue en el FRAGMENTO y no en la consulta importa: el
 * fragmento **no se manda en la cabecera `Referer`**, así que no se filtra a
 * la primera petición que salga. Sí queda en el historial del navegador, y por
 * eso la pantalla lo borra con `replaceState` en cuanto lo lee.
 *
 * Es también la razón de que `supabase.ts` tenga `detectSessionInUrl: false`
 * y siga teniéndolo: preferimos consumir el enlace en un solo sitio, a la
 * vista, que dejar que el cliente lo haga en cualquier página donde caiga.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ Y UN ENLACE VENCIDO NO ES «NO LLEGÓ NADA»
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Supabase devuelve el fallo en el mismo fragmento:
 * `#error=access_denied&error_code=otp_expired`. Si eso se leyera como «esta
 * URL no trae nada», la pantalla enseñaría el formulario de acceso y la
 * persona pensaría que el correo nunca llegó — cuando lo que pasó es que
 * tardó un día en abrirlo. Se distingue, y tiene su propio texto.
 */

/** Para qué era el enlace. Sale del propio enlace, no se supone. */
export type TipoDeEnlace = 'recovery' | 'invite' | 'signup' | 'email_change' | 'magiclink';

export type LlegadaDeCorreo =
  /** El vale de un solo uso. Hay que canjearlo con `verifyOtp`. */
  | { que: 'vale'; tokenHash: string; tipo: TipoDeEnlace }
  /** La sesión ya hecha, en el fragmento. Hay que fijarla y borrar la URL. */
  | { que: 'sesion'; accessToken: string; refreshToken: string; tipo: TipoDeEnlace | null }
  /** El enlace trae un fallo declarado: vencido, ya usado, denegado. */
  | { que: 'fallo'; motivo: MotivoDeEnlace }
  /** No hay nada de esto en la URL: se llegó por la puerta normal. */
  | { que: 'nada' };

export type MotivoDeEnlace =
  | 'vencido'      // `otp_expired` — lo más común con diferencia
  | 'usado'        // ya se canjeó una vez
  | 'denegado'     // `access_denied` sin más detalle
  | 'desconocido';

const TIPOS: readonly TipoDeEnlace[] = [
  'recovery', 'invite', 'signup', 'email_change', 'magiclink',
];

const comoTipo = (v: string | null): TipoDeEnlace | null =>
  (v !== null && (TIPOS as readonly string[]).includes(v) ? v as TipoDeEnlace : null);

export function leerLlegada(url: string): LlegadaDeCorreo {
  let consulta: URLSearchParams;
  let fragmento: URLSearchParams;
  try {
    const u = new URL(url);
    consulta = u.searchParams;
    // El fragmento viene con `#` delante y por dentro tiene forma de consulta.
    fragmento = new URLSearchParams(u.hash.replace(/^#/, ''));
  } catch {
    return { que: 'nada' };
  }

  // ▶ El fallo se mira PRIMERO.
  //
  // Un enlace vencido puede traer además restos del anterior en la URL —pasa
  // al recargar—, y atender el resto antes dejaría a alguien peleando con un
  // formulario que va a fallar al enviarse.
  const error = fragmento.get('error') ?? consulta.get('error');
  const codigo = fragmento.get('error_code') ?? consulta.get('error_code');
  if (error !== null || codigo !== null) {
    return { que: 'fallo', motivo: motivoDe(codigo, error) };
  }

  const vale = consulta.get('token_hash') ?? fragmento.get('token_hash');
  if (vale !== null && vale !== '') {
    // Sin `type` no se puede canjear: `verifyOtp` lo exige. Se cae a
    // `recovery`, que es el caso de lejos más común, y la pantalla igual
    // enseña el mismo formulario para todos los tipos.
    return {
      que: 'vale',
      tokenHash: vale,
      tipo: comoTipo(consulta.get('type') ?? fragmento.get('type')) ?? 'recovery',
    };
  }

  const acceso = fragmento.get('access_token');
  const refresco = fragmento.get('refresh_token');
  if (acceso !== null && acceso !== '' && refresco !== null && refresco !== '') {
    return {
      que: 'sesion',
      accessToken: acceso,
      refreshToken: refresco,
      tipo: comoTipo(fragmento.get('type')),
    };
  }

  return { que: 'nada' };
}

function motivoDe(codigo: string | null, error: string | null): MotivoDeEnlace {
  if (codigo === 'otp_expired') return 'vencido';
  if (codigo === 'otp_disabled' || codigo === 'validation_failed') return 'usado';
  if (error === 'access_denied') return 'denegado';
  return 'desconocido';
}

/**
 * Si una contraseña sirve, y por qué no.
 *
 * ▶ Devuelve una CLAVE y no una frase: del otro lado hay una pantalla
 * bilingüe. Y se comprueba acá, antes de mandarla, porque Supabase contesta
 * en inglés y con su propio texto —«Password should be at least 6
 * characters»—, que es justo lo que este producto no enseña.
 *
 * El mínimo son ocho y no seis: seis es el mínimo de Supabase, y aceptar el
 * mínimo del proveedor es dejar que otro decida una regla nuestra.
 */
export type MotivoDeClave = 'corta' | 'no_coincide' | 'vacia';

export function revisarClave(clave: string, repetida: string): MotivoDeClave | null {
  if (clave === '') return 'vacia';
  if (clave.length < 8) return 'corta';
  if (clave !== repetida) return 'no_coincide';
  return null;
}
